/**
 * Loading skeletons for every attractions route.
 *
 * Server components (no hooks) so each `loading.tsx` stays a 3-line file, the
 * same arrangement `app/restaurants/_components/Skeletons.tsx` uses.
 */

function Block({ h, w = '100%', r = 10 }: { h: number; w?: number | string; r?: number }) {
  return <div className="atr-skeleton" style={{ height: h, width: w, borderRadius: r }} />;
}

/**
 * One placeholder card. Exported so the listing can append a few INSIDE its
 * existing grid while infinite scroll loads the next page — a detached grid
 * below would not inherit the gap and would leave the last partial row short.
 */
export function CardSkeleton({ view = 'grid' }: { view?: 'grid' | 'list' }) {
  return (
    <div className={`atr-rcard atr-ticket${view === 'list' ? ' atr-rcard--row' : ''}`}>
      <Block h={190} r={16} />
      <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        <Block h={20} w="80%" />
        <Block h={14} w="55%" />
        <Block h={12} />
        <Block h={12} w="70%" />
        <Block h={22} w="40%" />
        <Block h={40} w={140} r={9999} />
      </div>
    </div>
  );
}

/**
 * The results grid as placeholders, for an IN-PAGE refetch.
 *
 * Shares `CardSkeleton` with the route-level `ListingSkeleton` so the two never
 * drift apart; this one renders only the cards, because the hero, sidebar and
 * toolbar are already on screen and must stay put while the grid reloads.
 */
export function ResultsSkeleton({
  view = 'grid',
  count = 6,
}: {
  view?: 'grid' | 'list';
  count?: number;
}) {
  return (
    <div className={view === 'grid' ? 'atr-grid' : 'atr-list'}>
      {Array.from({ length: count }, (_, i) => (
        <CardSkeleton key={i} view={view} />
      ))}
    </div>
  );
}

export function ListingSkeleton() {
  return (
    <div className="atr-page">
      <div className="atr-container" style={{ paddingTop: 24 }}>
        <Block h={384} r={20} />
      </div>
      <div className="atr-container" style={{ padding: '32px 20px 64px' }}>
        <div className="atr-listing">
          <div className="atr-filters" style={{ display: 'grid', gap: 18 }}>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} style={{ display: 'grid', gap: 10 }}>
                <Block h={18} w="60%" />
                <Block h={14} w="85%" />
                <Block h={14} w="75%" />
              </div>
            ))}
          </div>
          <div>
            <div className="atr-toolbar">
              <Block h={22} w={220} />
              <Block h={38} w={200} r={9999} />
            </div>
            {/* Grid is the default view, so the first paint matches it. */}
            <div className="atr-grid">
              {Array.from({ length: 6 }, (_, i) => (
                <CardSkeleton key={i} view="grid" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div className="atr-page">
      <div className="atr-container" style={{ padding: '28px 20px 64px', display: 'grid', gap: 22 }}>
        <Block h={18} w={180} />
        <Block h={40} w="55%" />
        <Block h={380} r={16} />
        <div className="atr-detail-strip">
          <Block h={150} r={20} />
          <Block h={150} r={20} />
        </div>
        <Block h={44} />
        <Block h={260} r={20} />
      </div>
    </div>
  );
}

export function BookingSkeleton() {
  return (
    <div className="atr-page">
      <div className="atr-container" style={{ padding: '28px 20px 64px' }}>
        <div className="atr-book">
          <div style={{ display: 'grid', gap: 22 }}>
            <Block h={40} w="45%" />
            <Block h={120} r={14} />
            <Block h={220} r={14} />
            <Block h={180} r={14} />
          </div>
          <Block h={430} r={20} />
        </div>
      </div>
    </div>
  );
}
